import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Award, Globe, Megaphone, TrendingUp, CheckCircle2, MessageSquare, ShieldCheck, Mail } from 'lucide-react';
import { PRODUCT_IMAGES } from '../data/images';
import company from '../data/company';

const Distributorship = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    targetCountry: '',
    email: '',
    phone: '',
    salesChannels: '',
    experienceYears: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanNum = company.whatsapp.replace(/[^0-9]/g, '');
    const text = `Hello RAYYAN DENIM,

INTERNATIONAL DISTRIBUTORSHIP APPLICATION:
- Full Name: ${formData.fullName}
- Company: ${formData.companyName}
- Target Country / Region: ${formData.targetCountry}
- Email: ${formData.email}
- Phone/WhatsApp: ${formData.phone}
- Distribution & Sales Channels: ${formData.salesChannels}
- Years in Apparel Corporate Operations: ${formData.experienceYears}
- Message / Strategy: ${formData.message}

We want to become your Official Country Distributor and market & sell RAYYAN DENIM in our country. Please get in touch!`;

    window.open(`https://wa.me/${cleanNum}?text=${encodeURIComponent(text)}`, '_blank');
    setSubmitted(true);
  };

  const partnerPillars = [
    {
      icon: <Globe size={26} />,
      title: "EXCLUSIVE COUNTRY RIGHTS",
      desc: "Single appointed distributor per country/territory. Protect your market exclusivity and build long-term brand equity in your region."
    },
    {
      icon: <Megaphone size={26} />,
      title: "FULL MARKETING & AD SUPPORT",
      desc: "Receive high-resolution editorial campaign photography, video lookbooks, social media assets, and point-of-sale branding to drive local marketing."
    },
    {
      icon: <TrendingUp size={26} />,
      title: "HIGH-MARGIN DISTRIBUTOR TIER",
      desc: "Highly competitive factory-direct pricing tiers engineered to ensure strong profit margins for your distribution and retail network."
    },
    {
      icon: <ShieldCheck size={26} />,
      title: "PRIORITY CONTAINER PRODUCTION",
      desc: "Fast-track production lines for country distributors to ensure rapid stock replenishment and seasonal collection launches."
    }
  ];

  const targetRegions = [
    { name: "NORTH & SOUTH AMERICA", status: "APPOINTING NOW", desc: "USA, Canada, Mexico, Brazil, Colombia, Chile" },
    { name: "EUROPE & UK", status: "APPOINTING NOW", desc: "Germany, UK, France, Italy, Spain, Netherlands, Poland" },
    { name: "MIDDLE EAST & GCC", status: "APPOINTING NOW", desc: "UAE, Saudi Arabia, Qatar, Kuwait, Oman, Bahrain" },
    { name: "AFRICA & ASIA PACIFIC", status: "APPOINTING NOW", desc: "Egypt, South Africa, Nigeria, Kenya, Australia, Malaysia" }
  ];

  return (
    <div style={{ paddingTop: 'calc(var(--nav-height) + 0px)', paddingBottom: '100px' }}>
      
      {/* Hero Banner */}
      <section style={{ position: 'relative', width: '100%', height: 'clamp(300px, 44vw, 520px)', overflow: 'hidden' }}>
        <img
          src={PRODUCT_IMAGES[2]}
          alt="RAYYAN DENIM Global Distributorship Partner Program"
          fetchpriority="high"
          loading="eager"
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(15,23,42,0.3) 0%, rgba(15,23,42,0.88) 100%)' }} />
        <div style={{ position: 'absolute', bottom: '40px', left: 0, right: 0 }}>
          <div className="container">
            <span className="section-badge" style={{ background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.4)', color: '#ffffff' }}>
              <Award size={13} />
              INTERNATIONAL DISTRIBUTOR OPPORTUNITY
            </span>
            <h1 style={{ fontSize: 'clamp(32px, 5.5vw, 64px)', fontFamily: 'var(--font-heading)', fontWeight: 900, textTransform: 'uppercase', color: '#ffffff', marginTop: '10px', lineHeight: 1.1 }}>
              BECOME AN OFFICIAL <span style={{ color: 'var(--accent-magenta)' }}>COUNTRY DISTRIBUTOR</span>
            </h1>
            <p style={{ fontSize: 'clamp(15px, 1.8vw, 19px)', color: 'rgba(255,255,255,0.88)', maxWidth: '760px', lineHeight: '1.6', marginTop: '10px' }}>
              We are actively appointing official Country Distributors & Regional Marketing Partners worldwide to promote, market, and sell <span className="brand-name">RAYYAN DENIM</span> products in international markets.
            </p>
          </div>
        </div>
      </section>

      {/* Program Highlights */}
      <section style={{ padding: '80px 0', background: '#ffffff' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 56px' }}>
            <span className="section-badge">GROW YOUR REGIONAL CORPORATE PRESENCE</span>
            <h2 className="section-title">
              WHY PARTNER AS A <span className="gradient-text"><span className="brand-name">RAYYAN DENIM</span> DISTRIBUTOR</span>
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px', lineHeight: 1.6 }}>
              We support our international distributors with factory-direct pricing, high-fashion marketing collateral, and exclusive territory rights to dominate local denim sales.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '28px' }}>
            {partnerPillars.map((pillar, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="glass-panel card-hover-effect"
                style={{ padding: '36px 26px', borderRadius: '16px', borderTop: '3px solid var(--accent-purple)' }}
              >
                <div
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '14px',
                    background: 'linear-gradient(135deg, var(--accent-purple), var(--accent-magenta))',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '22px',
                    boxShadow: '0 8px 20px rgba(224, 0, 135, 0.25)'
                  }}
                >
                  {pillar.icon}
                </div>
                <h3 style={{ fontSize: '18px', fontFamily: 'var(--font-heading)', fontWeight: 900, color: 'var(--text-main)', marginBottom: '12px', textTransform: 'uppercase' }}>
                  {pillar.title}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                  {pillar.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Open Territories Grid */}
      <section style={{ padding: '80px 0', background: '#f8fafc', borderTop: '1px solid rgba(15, 23, 42, 0.06)', borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '740px', margin: '0 auto 48px' }}>
            <span className="section-badge">GLOBAL TERRITORY AVAILABILITY</span>
            <h2 className="section-title">
              OPEN <span className="gradient-text">DISTRIBUTORSHIPS BY REGION</span>
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
              Applications now open for primary distributors in key international markets.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {targetRegions.map((region, idx) => (
              <div key={idx} className="glass-panel" style={{ padding: '28px 24px', borderRadius: '16px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '99px',
                    background: 'rgba(37, 211, 102, 0.12)',
                    color: '#15803d',
                    fontSize: '10.5px',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    marginBottom: '12px'
                  }}
                >
                  {region.status}
                </span>
                <h3 style={{ fontSize: '17px', fontFamily: 'var(--font-heading)', fontWeight: 900, color: 'var(--text-main)', marginBottom: '8px' }}>
                  {region.name}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                  {region.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Distributorship Application Form */}
      <section style={{ padding: '90px 0', background: '#ffffff' }}>
        <div className="container" style={{ maxWidth: '840px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="section-badge">PARTNER APPLICATION</span>
            <h2 className="section-title">
              APPLY FOR <span className="gradient-text">COUNTRY DISTRIBUTORSHIP</span>
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
              Fill out your company details below to initiate official territory rights discussion with our Global Expansion Board.
            </p>
          </div>

          <div className="glass-panel" style={{ padding: 'clamp(28px, 4vw, 48px)', borderRadius: '20px' }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(37, 211, 102, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#25d366', margin: '0 auto 20px' }}>
                  <CheckCircle2 size={36} />
                </div>
                <h3 style={{ fontSize: '24px', fontFamily: 'var(--font-heading)', color: 'var(--text-main)', marginBottom: '12px' }}>
                  APPLICATION SUBMITTED!
                </h3>
                <p style={{ color: 'var(--text-muted)', maxWidth: '480px', margin: '0 auto 24px' }}>
                  Your country distributorship application has been forwarded to our International Partnership Director. We will review your application and respond within 24 hours.
                </p>
                <button onClick={() => setSubmitted(false)} className="btn-secondary">
                  Submit Another Application
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  
                  {/* Full Name */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="e.g. Marc Laurent"
                    />
                  </div>

                  {/* Company Name */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Company / Firm Name *
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      required
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="e.g. European Denim Agency Ltd"
                    />
                  </div>

                  {/* Target Country */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Target Country / Region for Distributorship *
                    </label>
                    <input
                      type="text"
                      name="targetCountry"
                      required
                      value={formData.targetCountry}
                      onChange={handleChange}
                      placeholder="e.g. France / United Kingdom / UAE"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Official Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="laurent@eurodenim.fr"
                    />
                  </div>

                  {/* Phone / WhatsApp */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Phone / WhatsApp Number *
                    </label>
                    <input
                      type="text"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+33 1 42 68 55 00"
                    />
                  </div>

                  {/* Years in Corporate Operations */}
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Years in Apparel / Retail Corporate Operations
                    </label>
                    <input
                      type="text"
                      name="experienceYears"
                      value={formData.experienceYears}
                      onChange={handleChange}
                      placeholder="e.g. 8 Years"
                    />
                  </div>

                </div>

                {/* Sales & Marketing Channels */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Current Sales Network & Marketing Setup *
                  </label>
                  <input
                    type="text"
                    name="salesChannels"
                    required
                    value={formData.salesChannels}
                    onChange={handleChange}
                    placeholder="e.g. 45 Wholesale Retail Stockists + Online E-commerce Store"
                  />
                </div>

                {/* Message / Distribution Strategy */}
                <div style={{ marginBottom: '28px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Distribution Strategy & Market Vision *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Briefly describe how you plan to market and distribute RAYYAN DENIM in your country..."
                  />
                </div>

                {/* Submit Buttons */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <button type="submit" className="btn-primary" style={{ flexGrow: 1, padding: '16px 28px' }}>
                    <MessageSquare size={18} />
                    <span>APPLY VIA WHATSAPP</span>
                  </button>

                  <a
                    href={`mailto:${company.email}?subject=Country Distributorship Application`}
                    className="btn-secondary"
                    style={{ flexGrow: 1, padding: '16px 28px' }}
                  >
                    <Mail size={18} />
                    <span>APPLY VIA EMAIL</span>
                  </a>
                </div>

              </form>
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Distributorship;
